import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, TextField, Button, Alert, CircularProgress, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import AuthShell from '@/components/AuthShell'
import { resolveApiErrorMessage } from '@/utils/apiErrorMessage'
import type { FirstLoginCredentials } from '@/types/auth'

export default function FirstLoginPage() {
  const { t } = useTranslation('auth')
  const { t: tErrors } = useTranslation('errors')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { firstLogin } = useAuth()

  const [formData, setFormData] = useState<FirstLoginCredentials>({
    email: '',
    token: '',
    password: '',
    password_confirm: '',
  })

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const token = searchParams.get('token')
    const email = searchParams.get('email')

    if (!token || !email) {
      setError(t('invalidTokenUrl'))
      return
    }

    setFormData((prev) => ({
      ...prev,
      token,
      email,
    }))
  }, [searchParams, t])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    if (error) setError(null)
  }

  const validateForm = (): string | null => {
    if (!formData.password) {
      return t('passwordRequired')
    }

    if (formData.password.length < 8) {
      return t('passwordMinLength')
    }

    if (formData.password !== formData.password_confirm) {
      return t('passwordsDoNotMatch')
    }

    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const validationError = validateForm()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const user = await firstLogin(formData)
      console.log('First login successful:', user)
      setSuccess(true)

      // Researchers land on the researcher panel; respondents on theirs.
      // HomeRoute ("/") follows currentView, which firstLogin set from the
      // user's active_view (researcher by default for invited researchers).
      const destination = user?.is_researcher ? '/' : '/participant/dashboard'
      setTimeout(() => {
        navigate(destination)
      }, 2000)
    } catch (err: unknown) {
      console.error('First login failed:', err)

      if (err instanceof Error && 'data' in err) {
        const apiError = err as Error & { data?: Record<string, unknown> }
        if (apiError.data) {
          const data = apiError.data
          if (Array.isArray(data.password)) {
            setError(data.password[0] as string)
          } else if (Array.isArray(data.non_field_errors)) {
            setError(data.non_field_errors[0] as string)
          } else if (typeof data.detail === 'string') {
            setError(data.detail)
          } else {
            setError(err.message)
          }
        } else {
          setError(err.message)
        }
      } else {
        setError(resolveApiErrorMessage(err, tErrors) || t('firstLoginError'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <AuthShell
        title={t('passwordSetSuccess')}
        subtitle={t('redirectingToDashboard')}
        icon={<CheckCircleOutlineIcon />}
        iconTone="success"
      >
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      </AuthShell>
    )
  }

  if (!formData.token || !formData.email) {
    return (
      <AuthShell
        title={t('invalidLinkTitle')}
        subtitle={error || t('invalidLinkBody')}
        icon={<ErrorOutlineIcon />}
        iconTone="error"
      >
        <Button fullWidth variant="contained" size="large" onClick={() => navigate('/login')} sx={{ py: 1.25 }}>
          {t('goToLogin')}
        </Button>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title={t('firstLoginTitle')}
      subtitle={t('firstLoginSubtitle')}
      icon={<LockOutlinedIcon />}
    >
      <Typography
        variant="caption"
        align="center"
        color="text.secondary"
        sx={{ display: 'block', mb: 2, mt: -1 }}
      >
        {t('accountLabel', { email: formData.email })}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="password"
          label={t('newPassword')}
          name="password"
          type="password"
          autoComplete="new-password"
          autoFocus
          value={formData.password}
          onChange={handleInputChange}
          disabled={isLoading}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password_confirm"
          label={t('confirmPassword')}
          type="password"
          id="password_confirm"
          autoComplete="new-password"
          value={formData.password_confirm}
          onChange={handleInputChange}
          disabled={isLoading}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          sx={{ mt: 2.5, mb: 2, py: 1.25 }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : t('setUpPassword')}
        </Button>
        <Typography variant="caption" align="center" color="text.secondary" sx={{ display: 'block' }}>
          {t('linkExpiryHint')}
        </Typography>
      </Box>
    </AuthShell>
  )
}
